import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isSameMonth,
  isToday,
  isSameDay,
  addMonths,
  subMonths,
  isAfter,
  isBefore,
} from 'date-fns';
import { type DateRange } from 'react-day-picker';

type Mode = 'single' | 'range' | 'multiple';

interface CalendarProps {
  mode?: Mode;
  selected?: Date | DateRange | Date[];
  onSelect?: (value: Date | DateRange | Date[] | undefined) => void;
  className?: string;
  numberOfMonths?: number;
  defaultMonth?: Date;
  disabled?: boolean | ((date: Date) => boolean);
}

export const Calendar: React.FC<CalendarProps> = ({
  mode = 'single',
  selected,
  onSelect,
  className,
  numberOfMonths = 1,
  defaultMonth,
  disabled = false,
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(defaultMonth || today);

  const handleDayClick = (day: Date) => {
    if (onSelect) {
      if (mode === 'single') {
        onSelect(day);
      } else if (mode === 'range') {
        const currentSelectedRange = selected as DateRange;

        if (!currentSelectedRange || !currentSelectedRange.from) {
          onSelect({ from: day });
        } else if (currentSelectedRange.from && !currentSelectedRange.to) {
          if (isBefore(day, currentSelectedRange.from)) {
            onSelect({ from: day, to: currentSelectedRange.from });
          } else {
            onSelect({ from: currentSelectedRange.from, to: day });
          }
        } else {
          onSelect({ from: day });
        }
      } else if (mode === 'multiple') {
        const currentSelectedDates = selected as Date[] || [];
        const isSelected = currentSelectedDates.some((date) => isSameDay(date, day));

        if (isSelected) {
          onSelect(currentSelectedDates.filter((date) => !isSameDay(date, day)));
        } else {
          onSelect([...currentSelectedDates, day]);
        }
      }
    }
  };

  const isDateSelected = (day: Date) => {
    if (!selected) return false;

    if (mode === 'single') {
      return isSameDay(selected as Date, day);
    } else if (mode === 'range') {
      const range = selected as DateRange;
      if (!range) return false;
      
      return (
        range.from && isSameDay(range.from, day) ||
        range.to && isSameDay(range.to, day)
      );
    } else if (mode === 'multiple') {
      return (selected as Date[]).some((date) => isSameDay(date, day));
    }

    return false;
  };

  const isDateInRange = (day: Date) => {
    if (mode !== 'range' || !selected) return false;
    
    const range = selected as DateRange;
    if (!range || !range.from || !range.to) return false;
    
    return isAfter(day, range.from) && isBefore(day, range.to);
  };

  const isDateDisabled = (date: Date) => {
    if (typeof disabled === 'boolean') return disabled;
    if (typeof disabled === 'function') return disabled(date);
    return false;
  };

  const renderCalendar = (monthOffset = 0) => {
    const currentDate = addMonths(currentMonth, monthOffset);
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Calculate the start day of the week (0 = Sunday, 1 = Monday, etc.)
    const startDay = getDay(monthStart);
    
    // Build the calendar grid
    const daysInWeek = 7;
    const weeksInMonth = Math.ceil((daysInMonth.length + startDay) / daysInWeek);

    // Days of the week header
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    return (
      <div key={monthOffset} className="p-3">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-medium">{format(currentDate, 'MMMM yyyy')}</h2>
          {monthOffset === 0 && (
            <div className="flex space-x-1">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-1 rounded-md hover:bg-accent"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-1 rounded-md hover:bg-accent"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {daysOfWeek.map((day) => (
            <div key={day} className="text-center text-sm text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before the month starts */}
          {Array.from({ length: startDay }).map((_, index) => (
            <div key={`empty-start-${index}`} className="h-9" />
          ))}
          
          {/* Actual days in the month */}
          {daysInMonth.map((day) => {
            const isSelected = isDateSelected(day);
            const isInRange = isDateInRange(day);
            const dayDisabled = isDateDisabled(day);
            
            return (
              <button
                key={day.toISOString()}
                type="button"
                className={cn(
                  'h-9 w-9 rounded-md flex items-center justify-center text-sm',
                  isToday(day) && 'border border-accent',
                  isSelected && 'bg-primary text-primary-foreground',
                  isInRange && 'bg-accent/50',
                  !isSameMonth(day, currentDate) && 'text-muted-foreground opacity-50',
                  dayDisabled && 'opacity-30 cursor-not-allowed'
                )}
                disabled={dayDisabled}
                onClick={() => !dayDisabled && handleDayClick(day)}
              >
                {format(day, 'd')}
              </button>
            );
          })}
          
          {/* Empty cells for days after the month ends */}
          {Array.from({ length: (weeksInMonth * 7) - daysInMonth.length - startDay }).map((_, index) => (
            <div key={`empty-end-${index}`} className="h-9" />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={cn('bg-background p-3 rounded-md border', className)}>
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        {Array.from({ length: numberOfMonths }).map((_, i) => renderCalendar(i))}
      </div>
    </div>
  );
};

export default Calendar; 